'use client'
import { useEffect } from 'react'
import { Alert, AlertDescription, AlertIcon, Box, VStack } from '@chakra-ui/react'
import { BiCheckCircle } from 'react-icons/bi'
import { GrandHeading, GrandText } from 'ui'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { type BriqpaySession, type Option, type OrderDTO } from 'ecosystem'
import { useDispatch, useSelector } from 'shared-redux'
import { cleanCart, selectedCart } from 'shared-redux/state'
import { CONFIRMATION_CONTENT_MAX_W, ConfirmationOrderSummary } from './ConfirmationOrderSummary'

export interface BriqpayConfirmationOverrides {
  briqpayConfirmationTitle?: string
  briqpayConfirmationOrderNumberPrefix?: string
  briqpayConfirmationPending?: string
}

type BriqpayConfirmationProps = {
  briqpayData: BriqpaySession
  order?: Option<OrderDTO>
  onConfirmPurchase: () => void
}

/**
 * Briqpay confirmation, rendered from our own order rather than a provider widget.
 *
 * Briqpay's `thankYouSnippet` is deprecated — their support confirmed no merchant uses it
 * and that the confirmation page is the merchant's to build — and re-mounting the
 * checkout snippet instead makes the iframe retry its post-purchase redirect, which the
 * browser blocks as a cross-origin navigation without a user gesture.
 */
export const BriqpayConfirmation = ({
  briqpayData,
  order,
  onConfirmPurchase
}: BriqpayConfirmationProps) => {
  const dispatch = useDispatch()
  const overrides = useOverridesContext<keyof BriqpayConfirmationOverrides>()
  const { cartStateUuid } = useSelector(selectedCart)

  useEffect(() => {
    if (!briqpayData) {
      return
    }

    onConfirmPurchase()
  }, [briqpayData, onConfirmPurchase])

  // Clean the cart after payment confirmation, only if it is still the cart the session
  // was created for. Briqpay's `references` map replaces Qliro's MerchantProvidedMetadata.
  useEffect(() => {
    const sessionCartStateUuid = briqpayData?.references?.cartStateUuid

    if (briqpayData?.status !== 'completed' || !cartStateUuid || !sessionCartStateUuid) {
      return
    }

    try {
      if (sessionCartStateUuid === cartStateUuid) {
        dispatch(cleanCart())
      }
    } catch {
      void 0
    }
  }, [cartStateUuid, dispatch, briqpayData?.status, briqpayData?.references])

  if (!briqpayData) {
    return null
  }

  // Briqpay settles asynchronously: the shopper is done, but the PSP may not have
  // answered yet. Say so rather than presenting a payment as final when it is not.
  const isAwaitingPayment = briqpayData.moduleStatus?.payment?.orderStatus === 'order_pending'
  const transaction = briqpayData.data?.transactions?.[0]
  // With an orchestrator the method varies per order, so it is worth stating which one
  // actually took the payment.
  const paymentMethod = transaction?.pspDisplayName ?? transaction?.pspIntegrationName
  // The order's customer is absent on the first render — provisioning creates it and the
  // PUT response does not return it — so fall back to the session, which has the address
  // the shopper actually typed.
  const email = order?.customer?.email ?? briqpayData.data?.billing?.email ?? transaction?.email

  return (
    <VStack align="stretch" gap={6} w="100%">
      <VStack align="center" gap={3} textAlign="center">
        <Box color="status.success">
          <BiCheckCircle size={64} />
        </Box>

        <GrandHeading
          headingTag="h2"
          title={overrideText('Tack för din beställning!', overrides?.briqpayConfirmationTitle)}
        />

        {order?.orderNumber ? (
          <GrandText fontWeight="semibold">
            {overrideText('Ordernummer: ', overrides?.briqpayConfirmationOrderNumberPrefix)}
            {order.orderNumber}
          </GrandText>
        ) : null}
      </VStack>

      {isAwaitingPayment ? (
        <Alert borderRadius="md" maxW={CONFIRMATION_CONTENT_MAX_W} mx="auto" status="info" w="100%">
          <AlertIcon />
          <AlertDescription>
            {overrideText(
              'Din betalning behandlas och bekräftas inom kort.',
              overrides?.briqpayConfirmationPending
            )}
          </AlertDescription>
        </Alert>
      ) : null}

      {order ? (
        <ConfirmationOrderSummary email={email} order={order} paymentMethod={paymentMethod} />
      ) : null}
    </VStack>
  )
}
